import { ReactNode } from 'react';
import { MRT_ColumnDef, MRT_TableOptions, useMaterialReactTable } from 'material-react-table';
import { styled } from '@mui/material/styles';
import { Tooltip, tooltipClasses, TooltipProps } from '@mui/material';
import { InvoiceSearchResult } from 'src/generated/services/TFinancialApi';
import { formatDateString } from 'src/utils/date';

import { beautifyEnum, SimpleFormatNumber } from 'src/utils/utility';
import ReceiveSquare from '../../../../assets/images/icons/receive-square.svg';
import SendSquare from '../../../../assets/images/icons/send-square.svg';
import ArrangeSquare from '../../../../assets/images/icons/arrange-square.svg';
// Using the same icons for tooltip but with smaller dimensions
import ReceiveSquareTooltip from '../../../../assets/images/icons/receive-square_white.svg';
import SendSquareTooltip from '../../../../assets/images/icons/send-square_toottip.svg';
import ArrangeSquareTooltip from '../../../../assets/images/icons/arrange-square_tooltip.svg';

import DetailCell from '../../organisms/dashboard/DetailsCell';
import StatusChip from '../../organisms/dashboard/StatusChip';
import TableActions from '../../organisms/dashboard/TableActions';

export const useTableInvoice = (tableOptions: MRT_TableOptions<InvoiceSearchResult>) =>
  useMaterialReactTable({
    enableSorting: false,
    enablePinning: false,
    enableRowDragging: false,
    enableRowSelection: false,
    enableColumnActions: false,
    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableSortingRemoval: false,
    enableEditing: true,
    editDisplayMode: 'table',
    muiTableHeadCellProps: {
      align: 'left',
    },
    muiTableBodyCellProps: {
      align: 'left',
    },
    ...tableOptions,
  });

const iconStyle = { width: '24px', height: '25px' };
const iconTooltipStyle = { width: '20px', height: '20px' };
const minWidthCell = (minWidth: string) => ({ sx: { minWidth, verticalAlign: 'middle' } });

export const shipmentTypeIcons: Record<string, ReactNode> = {
  IMPORT: <img src={ReceiveSquare} alt='ReceiveSquare' style={iconStyle} />,
  EXPORT: <img src={SendSquare} alt='SendSquare' style={iconStyle} />,
  MULTI: <img src={ArrangeSquare} alt='ArrangeSquare' style={iconStyle} />,
};

export const shipmentTypeIconsTooltip: Record<string, ReactNode> = {
  IMPORT: <img src={ReceiveSquareTooltip} alt='ReceiveSquare' style={iconTooltipStyle} />,
  EXPORT: <img src={SendSquareTooltip} alt='SendSquare' style={iconTooltipStyle} />,
  MULTI: <img src={ArrangeSquareTooltip} alt='ArrangeSquare' style={iconTooltipStyle} />,
};

const BlackTooltip = styled(({ className, ...props }: TooltipProps) => <Tooltip {...props} classes={{ popper: className }} arrow />)(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#000',
    color: '#fff',
    padding: '6px 10px',
    borderRadius: '6px',
    fontSize: '10px',
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: '#000',
  },
}));

const renderCurrencyHeader = (label: string, currency: string) => (
  <div className='text-left leading-16 xl:leading-18'>
    <div>{label}</div>
    <div className='text-10 xl:text-12 text-[#808080] font-medium'>{currency}</div>
  </div>
);

const renderDateHeader = (label: string, dateRef: string) => (
  <div className='text-left leading-16 xl:leading-18'>
    <div>{label}</div>
    <div className='text-10 xl:text-12 text-[#808080] font-medium'>{beautifyEnum(dateRef)}</div>
  </div>
);

const renderShipmentTypeCell = ({ cell, row }: any) => {
  const shipmentType = row.original?.aggregateShipmentType || '';
  return (
    <BlackTooltip
      title={
        <div className='flex items-center gap-2'>
          {shipmentTypeIconsTooltip[shipmentType]} <span className='text-10 xl:tex1 text-white'>{shipmentType}</span>
        </div>
      }
      placement='bottom'
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%' }}>
        {(cell.getValue() as ReactNode) || null}
      </div>
    </BlackTooltip>
  );
};

export const useDefaultColumns = (currencyReference: string, dateReference: string): MRT_ColumnDef<InvoiceSearchResult>[] => [
  {
    enableEditing: false,
    enableSorting: false,
    accessorFn: (row: InvoiceSearchResult) => shipmentTypeIcons[row.aggregateShipmentType],
    accessorKey: 'aggregateShipmentType',
    header: ' ',
    size: 90,
    Cell: renderShipmentTypeCell,
    muiTableHeadCellProps: { sx: { minWidth: '60px', verticalAlign: 'middle', horizontalAlign: 'middle' } },
  },
  {
    enableEditing: false,
    enableSorting: true,
    accessorKey: 'invoiceNumber',
    header: 'INVOICE NO.',
    size: 20,
    muiTableHeadCellProps: minWidthCell('180px'),
  },
  {
    enableEditing: false,
    enableSorting: true,
    accessorKey: 'date',
    accessorFn: (row) => formatDateString(row?.date),
    header: 'DATE',
    Header: renderDateHeader('DATE', dateReference),
    size: 11,
    muiTableHeadCellProps: minWidthCell('110px'),
  },
  {
    enableEditing: false,
    enableSorting: true,
    accessorKey: 'status',
    accessorFn: (row) => {
      const { status, isFileProcessing } = row;
      let chipStatus = status as string;
      if (!status && !isFileProcessing) chipStatus = 'UNDER_REVIEW';
      return <StatusChip status={chipStatus} />;
    },
    header: 'STATUS',
    size: 120,
    muiTableHeadCellProps: minWidthCell('120px'),
  },
  {
    enableEditing: false,
    enableSorting: true,
    accessorKey: 'totalAmount',
    accessorFn: (row) => (row?.totalAmount ? SimpleFormatNumber(row?.totalAmount, currencyReference === 'JPY', 2) : '-'),
    header: 'TOTAL AMOUNT',
    Header: renderCurrencyHeader('TOTAL AMOUNT', currencyReference),
    size: 160,
    muiTableHeadCellProps: minWidthCell('160px'),
  },
  {
    enableEditing: false,
    enableSorting: true,
    accessorKey: 'deviationAmount',
    accessorFn: (row) => {
      if (row?.deviationAmount !== undefined && row?.deviationAmount !== null) {
        const formattedNumber = SimpleFormatNumber(row?.deviationAmount, currencyReference === 'JPY', 2);
        return <span className='text-[#E72547]'>{formattedNumber}</span>;
      }
      return '-';
    },
    header: 'DEVIATION AMOUNT',
    Header: renderCurrencyHeader('DEVIATION AMOUNT', currencyReference),
    size: 150,
    muiTableHeadCellProps: minWidthCell('150px'),
  },
  {
    enableEditing: false,
    enableSorting: true,
    accessorKey: 'shipmentNumber',
    accessorFn: (row) => {
      if (row?.shipmentNumber !== undefined && row?.shipmentNumber !== null) {
        return (
          <div className='flex items-center gap-12'>
            <p>{row?.shipmentNumber}</p>
            {row?.hasSpotShipment && <span className='text-[10px] py-2 px-4 font-semibold rounded-6 bg-neutral-10 text-neutral-50'>SPOT</span>}
          </div>
        );
      }
      return '-';
    },
    header: 'SHIPMENT NUMBER',
    size: 180,
    muiTableHeadCellProps: minWidthCell('180px'),
  },
  ...['tradeLane', 'service', 'incoterm', 'billedEntity', 'shipper', 'consignee'].map((key) => ({
    enableEditing: false,
    enableSorting: true,
    accessorKey: key,
    accessorFn: (row) => {
      const value = row[key as keyof InvoiceSearchResult];
      // Apply beautifyEnum to billedEntity, shipper, and consignee
      if (key === 'billedEntity' || key === 'shipper' || key === 'consignee') {
        return value ? beautifyEnum(value as string).toUpperCase() : '-';
      }
      return value || '-';
    },
    header: key.replace(/([A-Z])/g, ' $1').toUpperCase(),
    size: 110,
    muiTableHeadCellProps: minWidthCell('110px'),
  })),
  {
    enableEditing: false,
    enableSorting: false,
    accessorFn: (row) => <DetailCell data={row} isLeftAligned />,
    header: 'PENDING ITEMS',
    size: 140,
    muiTableHeadCellProps: minWidthCell('140px'),
    accessorKey: 'pendingItems',
  },
  {
    enableEditing: false,
    accessorFn: (row) => <TableActions data={row as InvoiceSearchResult} />,
    accessorKey: 'actions',
    header: ' ',
    enableSorting: false,
    size: 90,
    muiTableHeadCellProps: minWidthCell('90px'),
  },
];
